/**
 * useOwnersOfGoals — the React binding for the Owners of Goals section.
 *
 * Strict layering: the section calls THIS hook; the hook calls
 * {@link OwnersOfGoalsStore}; the store calls the api facade. The section never
 * imports the store or the api directly.
 *
 * Exposes the (seed-on-first-load) owner rows plus add/remove/edit helpers that
 * mutate local draft state, and a {@link UseOwnersOfGoalsResult.save} action
 * that validates and persists. Tenant scoping is automatic (the store routes by
 * the active team bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { OwnersOfGoalsStore } from './ownersOfGoalsStore';
import {
  addOwnerEntry,
  removeOwnerEntry,
  updateOwnerEntry,
  validateOwnerEntries,
} from './ownersOfGoalsModel';
import type { OwnerEntry } from './types';

export interface UseOwnersOfGoalsResult {
  /** The current (editable) owner rows. */
  entries: OwnerEntry[];
  updatedBy: string;
  lastUpdated: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Validation problems for the current rows (empty when valid). */
  problems: string[];
  /** True when the local rows differ from the last persisted snapshot. */
  dirty: boolean;
  refresh: () => Promise<void>;
  addRow: () => void;
  removeRow: (id: string) => void;
  editRow: (
    id: string,
    patch: Partial<Pick<OwnerEntry, 'stream' | 'owner'>>,
  ) => void;
  save: () => Promise<boolean>;
}

export function useOwnersOfGoals(): UseOwnersOfGoalsResult {
  const api = useApi();
  const store = useMemo(() => new OwnersOfGoalsStore(api), [api]);

  const [entries, setEntries] = useState<OwnerEntry[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const record = await store.load();
      setEntries(record.entries);
      setSavedSnapshot(JSON.stringify(record.entries));
      setUpdatedBy(record.updatedBy);
      setLastUpdated(record.lastUpdated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load owners of goals.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addRow = useCallback(() => setEntries((prev) => addOwnerEntry(prev)), []);

  const removeRow = useCallback(
    (id: string) => setEntries((prev) => removeOwnerEntry(prev, id)),
    [],
  );

  const editRow = useCallback(
    (id: string, patch: Partial<Pick<OwnerEntry, 'stream' | 'owner'>>) =>
      setEntries((prev) => updateOwnerEntry(prev, id, patch)),
    [],
  );

  const problems = useMemo(() => validateOwnerEntries(entries), [entries]);
  const dirty = useMemo(
    () => JSON.stringify(entries) !== savedSnapshot,
    [entries, savedSnapshot],
  );

  const save = useCallback(async () => {
    if (problems.length > 0) return false;
    setSaving(true);
    setError(null);
    try {
      const record = await store.save(entries);
      setEntries(record.entries);
      setSavedSnapshot(JSON.stringify(record.entries));
      setUpdatedBy(record.updatedBy);
      setLastUpdated(record.lastUpdated);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save owners of goals.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [store, entries, problems]);

  return {
    entries,
    updatedBy,
    lastUpdated,
    loading,
    saving,
    error,
    problems,
    dirty,
    refresh,
    addRow,
    removeRow,
    editRow,
    save,
  };
}
